import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, API } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { useUpgrade } from '../context/UpgradeContext';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { useProjectsAPI } from '../hooks/useProjectsAPI';
import { flattenAnnotatedPhoto } from '../components/PhotoAnnotator';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import QrScanner from '../components/QrScanner';
import { Printer, Link as LinkIcon, Copy, CheckCircle, QrCode, MapTrifold, PaperPlaneTilt, FileText, HouseLine, Warning, ArrowSquareOut } from '@phosphor-icons/react';
import EmptyState from '../components/EmptyState';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { compressImage } from '../lib/imageCompress';
import PinMarker from '../components/PinMarker';
import PinLegend from '../components/PinLegend';

export default function Report() {
    const { user } = useAuth();
    const ent = useEntitlements();
    const upgrade = useUpgrade();
    const store = useProjectsAPI(!!user?.user_id);
    const navigate = useNavigate();
    const [projectId, setProjectId] = useState(null);
    const [builderEmail, setBuilderEmail] = useState('');
    const [builderName, setBuilderName] = useState('');
    const [ownerMessage, setOwnerMessage] = useState('');
    const [shareLink, setShareLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [buildingLink, setBuildingLink] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    /** Last successfully-built PDF — surfaced as a persistent in-page card
     *  with a real <a download> link. Programmatic anchor clicks can be
     *  blocked by Chrome's popup heuristics on slow PDF builds, so the
     *  user always needs a physical link they can click. */
    const [readyPdf, setReadyPdf] = useState(null);  // { url, filename, builtAt, warnings }
    const reportRef = useRef(null);

    useEffect(() => {
        if (store.ready && !projectId && store.state.projects.length) {
            setProjectId(store.state.projects[0].id);
        }
    }, [store.ready, store.state.projects, projectId]);

    // Revoke any pending blob URL on unmount so we don't leak memory.
    useEffect(() => {
        return () => {
            if (readyPdf?.url) URL.revokeObjectURL(readyPdf.url);
        };
    }, [readyPdf?.url]);

    const project = useMemo(() => store.state.projects.find(p => p.id === projectId), [store.state.projects, projectId]);

    if (!store.ready) return null;
    if (store.state.projects.length === 0) {
        return (
            <div className="min-h-screen bg-white pb-24">
                <AppHeader title="Report" />
                <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
                    <EmptyState
                        icon={FileText}
                        eyebrow="Report"
                        title="Nothing to report on yet"
                        body="Once you've added a property and logged a few defects, this is where you'll build your branded PDF, email it to your builder, or share a live reply link."
                        cta={{
                            label: 'Add a property',
                            icon: HouseLine,
                            onClick: () => navigate('/app'),
                            testId: 'empty-report-add-property',
                        }}
                        testId="empty-report"
                    />
                </main>
                <BottomNav />
            </div>
        );
    }
    if (!project) return null;

    const out = project.snags.filter(s => s.status === 'outstanding');
    const done = project.snags.filter(s => s.status === 'completed');
    const stats = { outstanding: out.length, completed: done.length, total: project.snags.length };

    // Phase 1.7 — escalate to NHBC. NHBC's Buildmark claim intake is a
    // multi-step web form (not an email address), so we open the form in
    // a new tab. The homeowner steps through the form using the property
    // + defect summary we surface beneath the button as their reference.
    // Form URL verified by the user (Buildmark customer claim survey).
    const NHBC_CLAIM_FORM_URL = 'https://survey.euro.confirmit.com/wix/5/p524838690945.aspx';
    const disputedOrRejected = project.snags.filter(
        s => s.builder_response === 'rejected' || s.disputed,
    );

    /** Build the PDF mathematically via jsPDF. Page 1+ holds the snag list
     *  with inline photo thumbnails; the floorplans (with pin overlays) are
     *  rendered last, one per page. No DOM screenshotting — no html2canvas.
     *  Returns { blob, failedFloorplans[] } so callers can surface partial
     *  failures (e.g. a floorplan image that couldn't be embedded).
     */
    const generatePdfBlob = async () => {
        if (!project) throw new Error('Report not ready');
        return buildPdfDocument({ project, user, out, done, shareLink });
    };

    /** Synchronous helper that attempts a programmatic download via a fresh
     *  anchor click. Works on the happy path, but Chrome's popup heuristics
     *  silently drop it after slow PDF builds. We always also surface a
     *  persistent in-page link as a guaranteed fallback. */
    const triggerDownload = (url, filename) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    /** Convert a Blob to a base64 data: URL.  Data URLs sidestep two
     *  Chrome restrictions that blob: URLs hit:
     *    1. Iframed contexts without `allow-downloads` sandbox permission
     *       throw "Not allowed to load local resource: blob:..." for blob
     *       URL navigation.
     *    2. New-tab navigation (`target="_blank"`) to a blob URL is
     *       cross-process and the new renderer can't see the blob registry.
     *  Data URLs are inlined into the href so they're always navigable. */
    const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

    /** Download-to-disk path. Multi-strategy because no single download
     *  trigger works in every browser context anymore:
     *    1. jsPDF's built-in `doc.save()` — has years of cross-browser
     *       quirks baked in and works in Chrome/Safari/Firefox/Edge top-level.
     *    2. Persistent `<a download>` link in the page — the user physically
     *       clicks it, so no popup blocker can intercept.
     *    3. `<a target="_blank">` with a data: URL fallback — works inside
     *       iframes that lack `allow-downloads` (Emergent preview wrapper,
     *       embedded views).
     */
    const downloadPdf = async () => {
        if (!project) return;
        const toastId = toast.loading('Building PDF…');
        try {
            const { doc, blob, failedFloorplans } = await generatePdfBlob();
            const filename = `defectr-${project.name.replace(/\W+/g, '-').toLowerCase()}.pdf`;
            const url = URL.createObjectURL(blob);

            // Build a base64 data: URL up-front so iframed users have a
            // working link without needing a second click. Done in
            // parallel with the download attempt so we don't block.
            const dataUrlPromise = blobToDataUrl(blob).catch(() => null);

            // Revoke any previous URL — only one ready PDF at a time.
            if (readyPdf?.url) URL.revokeObjectURL(readyPdf.url);

            // Strategy 1 — jsPDF's built-in saver. Has the most thorough
            // cross-browser quirks handling of any save path we have. We
            // ONLY hedge with the manual anchor click if doc.save throws
            // outright; otherwise modern Chrome/Safari fire BOTH saves
            // and the user sees the same file download twice.
            let savedNatively = false;
            try {
                doc.save(filename);
                savedNatively = true;
            } catch (e) {
                console.warn('[pdf] doc.save failed', e);
            }

            // Strategy 2 — fallback anchor click only if Strategy 1 errored.
            // The persistent <a download> card (Strategy 3) is always rendered
            // as a final-resort visible link regardless.
            if (!savedNatively) {
                triggerDownload(url, filename);
            }

            // Resolve the data URL and publish all three handles to state
            // so the persistent download card renders the real anchors.
            const dataUrl = await dataUrlPromise;
            setReadyPdf({
                url,
                dataUrl,
                filename,
                builtAt: Date.now(),
                warnings: failedFloorplans,
                inIframe: typeof window !== 'undefined' && window.self !== window.top,
            });

            // Consume the free-PDF credit AFTER the download has fired.
            try {
                await axios.post(
                    `${API}/entitlements/consume-free-pdf`,
                    { property_id: project.id },
                    { withCredentials: true },
                );
            } catch (consumeErr) {
                if (consumeErr?.response?.status === 402) {
                    const code = consumeErr.response.data?.detail?.code || 'pdf_cap_reached';
                    upgrade?.show?.({ property: project, reason: code });
                }
            }

            const baseMsg = failedFloorplans.length
                ? `PDF ready — ${failedFloorplans.length} floorplan${failedFloorplans.length === 1 ? '' : 's'} couldn't be embedded.`
                : 'PDF ready.';
            toast.success(`${baseMsg} If your download didn't start, scroll up and tap the green Save PDF link.`, {
                id: toastId,
                duration: 8_000,
            });

            // Scroll the persistent download card into view so the user
            // finds it without hunting.
            setTimeout(() => {
                document.querySelector('[data-testid="pdf-ready-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
        } catch (err) {
            console.error('[pdf] download failed', err);
            toast.error(`Could not build PDF: ${err?.message || 'unknown error'}`, { id: toastId });
        }
    };

    /** Send the branded email + PDF attachment via Resend. */
    const sendEmail = async () => {
        const to = builderEmail.trim();
        if (!to || !/@/.test(to)) { toast.error('Enter the builder email'); return; }
        if (!project) return;
        setSendingEmail(true);
        const toastId = toast.loading('Building PDF…');
        try {
            const { blob } = await generatePdfBlob();
            toast.loading('Sending email…', { id: toastId });
            const fd = new FormData();
            fd.append('property_id', project.id);
            fd.append('to', to);
            fd.append('subject', `Snag list for ${project.name} — ${stats.total} defect${stats.total === 1 ? '' : 's'}`);
            fd.append('custom_message', ownerMessage.trim());
            fd.append('outstanding_count', String(stats.outstanding));
            fd.append('completed_count', String(stats.completed));
            fd.append('total_count', String(stats.total));
            if (user?.email) fd.append('cc', user.email);
            fd.append('pdf_file', blob, `defectr-${project.name.replace(/\W+/g, '-').toLowerCase()}.pdf`);

            await axios.post(`${API}/report/email`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Email sent — builder will receive the PDF shortly.', { id: toastId });
            setBuilderEmail('');
            setOwnerMessage('');
        } catch (err) {
            if (err?.response?.status === 402) {
                toast.dismiss(toastId);
                const code = err.response.data?.detail?.code || 'share_locked';
                upgrade?.show?.({ property: project, reason: code });
                return;
            }
            console.error('Email send failed', err);
            toast.error(err?.response?.data?.detail || 'Could not send email.', { id: toastId });
        } finally {
            setSendingEmail(false);
        }
    };

    const buildShareLink = async () => {
        setBuildingLink(true);
        try {
            const reportId = (crypto.randomUUID().replace(/-/g, '')).slice(0, 8);

            // Compress every floorplan that has at least one pin on it so the
            // builder can see the plans with pin overlays.
            const usedFloorIds = new Set(
                project.snags.filter(s => s.pin && s.pin.floorId).map(s => s.pin.floorId)
            );
            const compressedFloors = await Promise.all(
                (project.floorplans || [])
                    .filter(f => usedFloorIds.has(f.id))
                    .map(async f => ({
                        id: f.id,
                        name: f.name,
                        // Floorplans are line drawings — PNG (lossless) keeps
                        // walls / room labels crisp. PNG of line art often
                        // compresses smaller than a high-quality JPEG of the
                        // same, so payload stays lean.
                        image: f.image ? await compressImage(f.image, 2000, 1.0, 'png') : null,
                    }))
            );

            // Compress snag photos hard so the builder can see them in the report.
            // Small — we're targeting thumbnails, not full resolution.
            const compressedSnags = await Promise.all(
                project.snags.map(async s => ({
                    id: s.id.slice(0, 8),
                    title: s.title, notes: s.notes, status: s.status,
                    room: project.rooms.find(r => r.id === s.roomId)?.name || '—',
                    floor: s.pin?.floorId ? (project.floorplans?.find(f => f.id === s.pin.floorId)?.name || null) : null,
                    pin: s.pin ?? null,
                    pinNumber: s.pinNumber ?? null,
                    photo: s.photo ? await compressImage(s.photo, 900, 0.55) : null,
                    // Carry vector annotations through to the share portal
                    // so the AnnotationOverlay there can render them on
                    // top of the raw JPEG (crisper than baking them into
                    // the photo, smaller payload too). The PDF render
                    // still flattens via flattenAnnotatedPhoto.
                    photo_annotations: s.photo_annotations || [],
                    // Include the AI verdict so builders see the same
                    // severity / cause / fix / NHBC ref / cost in the portal.
                    ai_analysis: s.ai_analysis ? {
                        cause: s.ai_analysis.cause,
                        severity: s.ai_analysis.severity,
                        suggested_fix: s.ai_analysis.suggested_fix,
                        cost_band: s.ai_analysis.cost_band,
                        responsibility: s.ai_analysis.responsibility,
                        nhbc_reference: s.ai_analysis.nhbc_reference || null,
                        confidence: s.ai_analysis.confidence,
                    } : null,
                }))
            );

            const payload = {
                v: 2,
                r: reportId,
                propertyId: project.id,
                name: project.name,
                address: project.address,
                date: new Date().toISOString(),
                reporter: { name: user?.name, email: user?.email },
                rooms: project.rooms.map(r => ({ id: r.id, name: r.name })),
                floorplans: compressedFloors,
                snags: compressedSnags,
            };
            try {
                localStorage.setItem(`defectr:report:${reportId}`, JSON.stringify({
                    projectId: project.id,
                    userId: user?.user_id,
                    createdAt: Date.now(),
                }));
            } catch { /* noop */ }

            let res;
            try {
                res = await axios.post(`${API}/share`, payload, { withCredentials: true });
            } catch (postErr) {
                try { localStorage.removeItem(`defectr:report:${reportId}`); } catch { /* noop */ }
                if (postErr?.response?.status === 402) {
                    const code = postErr.response.data?.detail?.code || 'share_locked';
                    upgrade?.show?.({ property: project, reason: code });
                    return;  // buildingLink reset in the finally
                }
                throw postErr;
            }
            const shortId = res.data?.short_id;
            if (!shortId) {
                try { localStorage.removeItem(`defectr:report:${reportId}`); } catch { /* noop */ }
                throw new Error('No short_id returned');
            }
            const url = `${window.location.origin}/shared/${shortId}`;
            setShareLink(url);
            toast.success('Share link ready');
        } catch (err) {
            console.error('Build share link failed', err);
            toast.error('Could not build share link. Try again.');
        } finally {
            setBuildingLink(false);
        }
    };

    /** Guarded open of the QR scanner for builder replies. Architect +
     *  unlocked properties always pass. */
    const handleOpenScanner = async () => {
        if (!project?.id) { toast.error('Choose a property first.'); return; }
        try {
            await axios.post(
                `${API}/entitlements/check-scan-reply`,
                { property_id: project.id },
                { withCredentials: true },
            );
            setScannerOpen(true);
        } catch (err) {
            if (err?.response?.status === 402) {
                const code = err.response.data?.detail?.code || 'reply_portal_locked';
                upgrade?.show?.({ property: project, reason: code });
                return;
            }
            toast.error('Could not open scanner.');
        }
    };

    const applyBuilderReply = (raw) => {
        setScannerOpen(false);
        let data;
        try {
            const json = decodeURIComponent(escape(atob(raw.trim())));
            data = JSON.parse(json);
        } catch {
            toast.error('Could not read that reply code.');
            return;
        }
        if (!data || data.v !== 1 || !Array.isArray(data.f)) {
            toast.error('Invalid reply payload.');
            return;
        }
        const mapRaw = localStorage.getItem(`defectr:report:${data.r}`);
        if (!mapRaw) {
            toast.error('No matching report on this device.');
            return;
        }
        let map;
        try { map = JSON.parse(mapRaw); } catch { map = null; }
        if (!map?.projectId) {
            toast.error('Report mapping is corrupted.');
            return;
        }
        const targetProject = store.state.projects.find(p => p.id === map.projectId);
        if (!targetProject) {
            toast.error('That property no longer exists.');
            return;
        }

        let fixedCount = 0;
        let reopenCount = 0;
        data.f.forEach(prefix => {
            const snag = targetProject.snags.find(s => s.id.startsWith(prefix));
            if (snag && snag.status !== 'completed') {
                store.updateSnag(targetProject.id, snag.id, { status: 'completed' });
                fixedCount++;
            }
        });
        (Array.isArray(data.u) ? data.u : []).forEach(prefix => {
            const snag = targetProject.snags.find(s => s.id.startsWith(prefix));
            if (snag && snag.status !== 'outstanding') {
                store.updateSnag(targetProject.id, snag.id, { status: 'outstanding' });
                reopenCount++;
            }
        });

        if (fixedCount + reopenCount === 0) {
            toast('Reply applied. Nothing changed.');
        } else {
            toast.success(`Builder reply applied — ${fixedCount} fixed${reopenCount ? `, ${reopenCount} reopened` : ''}.`);
        }
    };

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
    };

    return (
        <div className="min-h-screen bg-white pb-24">
            <AppHeader title="Report" subtitle={project.name} />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                {store.state.projects.length > 1 && (
                    <div className="mb-4 no-print">
                        <div className="label-tiny mb-2">Property</div>
                        <select
                            data-testid="report-project-select"
                            value={projectId}
                            onChange={(e) => setProjectId(e.target.value)}
                            className="w-full border border-[#18181B] px-3 py-2 bg-white"
                        >
                            {store.state.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                )}

                {/* Send actions */}
                <div className="no-print">
                    <div className="label-tiny mb-2">Email PDF report to builder</div>
                    <div className="border border-[#18181B] divide-y divide-[#18181B]">
                        <input
                            data-testid="builder-email-input"
                            value={builderEmail}
                            onChange={(e) => setBuilderEmail(e.target.value)}
                            placeholder="builder@example.com"
                            className="w-full px-3 py-3 outline-none"
                            type="email"
                        />
                        <textarea
                            data-testid="builder-message-input"
                            value={ownerMessage}
                            onChange={(e) => setOwnerMessage(e.target.value)}
                            placeholder="Optional message to the builder…"
                            rows={2}
                            className="w-full px-3 py-2 outline-none text-sm resize-none"
                            maxLength={500}
                        />
                    </div>
                    <button
                        onClick={sendEmail}
                        disabled={sendingEmail || !builderEmail.trim()}
                        data-testid="send-email-btn"
                        className={`w-full mt-3 py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 ${
                            sendingEmail ? 'bg-[#A1A1AA] text-white cursor-wait'
                            : !builderEmail.trim() ? 'bg-[#A1A1AA] text-white cursor-not-allowed'
                            : 'bg-[#1FA5CF] text-white hover:bg-[#1787AB]'
                        }`}
                    >
                        <PaperPlaneTilt size={16} weight="bold" />
                        {sendingEmail ? 'Sending…' : 'Send email with PDF'}
                    </button>

                    <div className="grid sm:grid-cols-2 gap-2 mt-3">
                        <button
                            onClick={downloadPdf}
                            data-testid="download-pdf-btn"
                            className="border border-[#18181B] py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2"
                        >
                            <Printer size={16} weight="bold" /> Download PDF
                        </button>
                        <button
                            onClick={buildShareLink}
                            data-testid="build-share-btn"
                            disabled={buildingLink}
                            className={`border border-[#18181B] py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 ${buildingLink ? 'opacity-60 cursor-wait' : 'hover:bg-[#F4F4F5]'}`}
                        >
                            <LinkIcon size={16} weight="bold" /> {buildingLink ? 'Building…' : 'Build share link'}
                        </button>
                    </div>
                    <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#52525B]" data-testid="qr-hint">
                        {shareLink
                            ? '✓ Share link will be printed as a QR on your PDF'
                            : 'Build a share link first to include a scannable QR in your PDF'}
                    </p>

                    {/* Phase 1.7 — Open NHBC Buildmark claim form.
                        Surfaces only when there's an actual escalation to
                        make (≥1 rejected or disputed defect). NHBC's
                        intake is a multi-step web survey, so we open it
                        in a new tab and surface the property/builder
                        details + defect counts beneath as the
                        homeowner's reference while they fill it in. */}
                    {disputedOrRejected.length > 0 && (
                        <div
                            className="mt-4 border-2 border-[#2563EB] bg-[#DBEAFE] p-4"
                            data-testid="nhbc-escalate-card"
                        >
                            <div className="flex items-start gap-3">
                                <Warning size={22} weight="fill" color="#2563EB" className="mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold text-sm">
                                        Stuck on a builder rejection?
                                    </div>
                                    <p className="mt-1 text-xs text-[#1E3A8A]">
                                        You have <strong>{disputedOrRejected.length}</strong> rejected or disputed
                                        defect{disputedOrRejected.length === 1 ? '' : 's'}. Start your NHBC
                                        Buildmark claim — the form will open in a new tab. Use the
                                        details below to fill it in.
                                    </p>

                                    {/* What you'll need — quick checklist */}
                                    <div className="mt-3 bg-white border border-[#BFDBFE] p-3 text-[11px] text-[#1E3A8A]">
                                        <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1E3A8A] mb-2">
                                            Have these ready
                                        </div>
                                        <ul className="space-y-1">
                                            <li className="flex items-start gap-2">
                                                <CheckCircle size={12} weight="fill" color={readyPdf ? '#1FA5CF' : '#94A3B8'} className="mt-0.5 flex-none" />
                                                <span>
                                                    Your DefectR PDF report
                                                    {readyPdf
                                                        ? ' — already built, attach when prompted.'
                                                        : ' — tap "Build PDF" further down first, then come back.'}
                                                </span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <CheckCircle size={12} weight="fill" color="#1FA5CF" className="mt-0.5 flex-none" />
                                                <span>Property: <strong>{project.name || '—'}</strong>{project.address ? <>, {project.address}</> : null}</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <CheckCircle size={12} weight="fill" color="#1FA5CF" className="mt-0.5 flex-none" />
                                                <span>Developer / builder: <strong>{project.developer_name || 'Add this on the project page'}</strong></span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <CheckCircle size={12} weight="fill" color="#1FA5CF" className="mt-0.5 flex-none" />
                                                <span>
                                                    Defects to escalate:&nbsp;
                                                    <strong>{project.snags.filter(s => s.builder_response === 'rejected').length}</strong> rejected,&nbsp;
                                                    <strong>{project.snags.filter(s => s.disputed).length}</strong> disputed
                                                </span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <CheckCircle size={12} weight="fill" color="#1FA5CF" className="mt-0.5 flex-none" />
                                                <span>Your NHBC Buildmark policy number (from your warranty docs)</span>
                                            </li>
                                        </ul>
                                    </div>

                                    <a
                                        href={NHBC_CLAIM_FORM_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="open-nhbc-claim-form"
                                        className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#2563EB] text-white font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1D4ED8] active:scale-95 transition-all duration-150 no-underline"
                                    >
                                        <ArrowSquareOut size={14} weight="bold" />
                                        Open NHBC Claim Form
                                    </a>
                                    <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#1E3A8A]">
                                        Opens in a new tab · NHBC Buildmark customer claim survey
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {readyPdf && (
                        <div
                            className="mt-4 border-2 border-[#1FA5CF] bg-[#E8F5FB] p-4"
                            data-testid="pdf-ready-card"
                        >
                            <div className="flex items-start gap-3">
                                <CheckCircle size={22} weight="fill" color="#1FA5CF" className="mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold text-sm">Your PDF is ready</div>
                                    <p className="mt-1 text-xs text-[#52525B]">
                                        If the download didn&apos;t start automatically, tap the button below.
                                        {readyPdf.warnings?.length > 0 && (
                                            <span className="block mt-1 text-[#E53935]">
                                                Note: {readyPdf.warnings.length} floorplan{readyPdf.warnings.length === 1 ? '' : 's'} couldn&apos;t be embedded ({readyPdf.warnings.join(', ')}).
                                            </span>
                                        )}
                                        {readyPdf.inIframe && (
                                            <span className="block mt-1 text-[#B45309] bg-[#FEF3C7] border border-[#F59E0B] px-2 py-1 mt-2">
                                                Heads up — you&apos;re viewing DefectR inside another page (preview / iframe).
                                                Some browsers block downloads in this mode.
                                                If the buttons below don&apos;t work, open DefectR in its own tab and try again.
                                            </span>
                                        )}
                                    </p>
                                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                        {/* Real <a download> link with the data: URL when we
                                            have one — data: URLs survive iframe sandboxing
                                            and cross-process new-tab navigation that blob:
                                            URLs cannot. Falls back to blob: URL otherwise. */}
                                        <a
                                            href={readyPdf.dataUrl || readyPdf.url}
                                            download={readyPdf.filename}
                                            data-testid="pdf-ready-save-link"
                                            className="bg-[#1FA5CF] text-white px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 no-underline"
                                        >
                                            <Printer size={14} weight="bold" /> Save PDF
                                        </a>
                                        {/* Open-in-new-tab uses the data: URL too — Chrome can
                                            navigate cross-process to a data: URL but not to
                                            a blob: URL created in another renderer. */}
                                        <a
                                            href={readyPdf.dataUrl || readyPdf.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="pdf-ready-open-link"
                                            className="border border-[#18181B] px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 no-underline text-[#09090B]"
                                        >
                                            <FileText size={14} weight="bold" /> Open in new tab
                                        </a>
                                    </div>
                                    <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA]">
                                        Filename: {readyPdf.filename}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleOpenScanner}
                        data-testid="scan-reply-btn"
                        className="w-full mt-3 bg-[#09090B] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#27272A] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2"
                    >
                        <QrCode size={16} weight="bold" /> Scan builder reply
                    </button>
                    {shareLink && (
                        <div className="mt-3 border border-[#E4E4E7] p-3 flex items-center gap-2 fade-up">
                            <div className="flex-1 min-w-0 font-mono text-xs text-[#52525B] truncate" data-testid="share-link">{shareLink}</div>
                            <button onClick={copyLink} data-testid="copy-share-btn" className="bg-[#1FA5CF] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1">
                                {copied ? <><CheckCircle size={14} weight="fill" /> Copied</> : <><Copy size={14} /> Copy</>}
                            </button>
                        </div>
                    )}
                </div>

                {/* Printable report */}
                <section ref={reportRef} className="mt-8 border border-[#18181B] p-6" data-testid="report-sheet">
                    <div className="flex items-start justify-between">
                        <div>
                            <img
                                src="/defectr-logo.png"
                                alt="DefectR"
                                className="h-12 w-auto object-contain"
                            />
                            <h1 className="font-display text-3xl font-bold mt-3">Snag Report</h1>
                            <p className="label-tiny mt-1">Defects Rectified · {new Date().toLocaleDateString()}</p>
                        </div>
                        <div className="text-right text-xs font-mono uppercase tracking-[0.18em]">
                            <div>{project.name}</div>
                            {project.address && <div className="text-[#52525B]">{project.address}</div>}
                            <div className="text-[#52525B]">By {user?.name}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 mt-6 border-t border-b border-[#18181B]">
                        <div className="p-3 border-r border-[#18181B]">
                            <div className="label-tiny">Outstanding</div>
                            <div className="font-display text-2xl font-bold text-[#E53935]">{out.length}</div>
                        </div>
                        <div className="p-3">
                            <div className="label-tiny">Completed</div>
                            <div className="font-display text-2xl font-bold text-[#00875A]">{done.length}</div>
                        </div>
                    </div>

                    <SnagSection title="Outstanding defects" items={out} rooms={project.rooms} floorplans={project.floorplans || []} color="#E53935" />
                    <SnagSection title="Completed defects" items={done} rooms={project.rooms} floorplans={project.floorplans || []} color="#00875A" />
                    <FloorplansBlock project={project} />
                </section>
            </main>
            <BottomNav />
            {scannerOpen && (
                <QrScanner onResult={applyBuilderReply} onClose={() => setScannerOpen(false)} />
            )}
        </div>
    );
}

function SnagSection({ title, items, rooms, floorplans, color }) {
    if (items.length === 0) return null;
    const pinnedSuffix = (pin, floorName) => {
        if (!pin) return '';
        if (floorName) return ` · pinned on ${floorName}`;
        return ' · pinned on plan';
    };
    return (
        <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 inline-block" style={{ background: color }} />
                <h3 className="font-display font-semibold uppercase tracking-wider text-sm">{title}</h3>
                <span className="font-mono text-xs text-[#52525B]">({items.length})</span>
            </div>
            <ul className="divide-y divide-[#E4E4E7] border border-[#E4E4E7]">
                {items.map((s, i) => {
                    const room = rooms.find(r => r.id === s.roomId)?.name || '—';
                    const floorName = s.pin?.floorId ? (floorplans?.find(f => f.id === s.pin.floorId)?.name || null) : null;
                    return (
                        <li key={s.id} className="p-3 flex items-start gap-3">
                            <span className="font-mono text-xs text-[#52525B] w-6 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                            {s.photo && (
                                <div className="relative w-14 h-14 shrink-0">
                                    <img src={s.photo} alt="" className="w-full h-full object-cover border border-[#E4E4E7]" />
                                    {/* Mirror the snag list page — show a cyan
                                        pencil corner badge on thumbs that
                                        carry vector annotations. The full
                                        marked-up image only renders in the
                                        flattened PDF / portal export; in
                                        the in-app preview the badge is
                                        enough cue and keeps the thumb light. */}
                                    {s.photo_annotations?.length > 0 && (
                                        <span
                                            className="absolute bottom-0.5 right-0.5 bg-[#1FA5CF] text-white font-mono text-[8px] leading-none px-1 py-0.5"
                                            title={`${s.photo_annotations.length} annotation(s)`}
                                            data-testid={`report-thumb-annotations-${s.id}`}
                                        >
                                            ✎
                                        </span>
                                    )}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-semibold">{s.title}</div>
                                <div className="text-xs text-[#52525B]">
                                    {room}{s.notes ? ` · ${s.notes}` : ''}{pinnedSuffix(s.pin, floorName)}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}


function FloorplansBlock({ project }) {
    const floorplans = (project.floorplans || []).filter(f => f.image);
    if (floorplans.length === 0) return null;

    return (
        <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
                <MapTrifold size={16} weight="bold" color="#1FA5CF" />
                <h3 className="font-display font-semibold uppercase tracking-wider text-sm">Floor plans</h3>
                <span className="font-mono text-xs text-[#52525B]">({floorplans.length})</span>
            </div>
            <div className="space-y-6">
                {floorplans.map(floor => {
                    const pinsOnFloor = project.snags.filter(s => s.pin && s.pin.floorId === floor.id);
                    return (
                        <div key={floor.id} className="border border-[#18181B] break-inside-avoid">
                            <div className="px-3 py-2 border-b border-[#18181B] flex items-center justify-between">
                                <div className="font-display font-semibold">{floor.name}</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                                    {pinsOnFloor.length} pin{pinsOnFloor.length === 1 ? '' : 's'}
                                </div>
                            </div>
                            <div className="relative bg-[#F4F4F5]">
                                <img src={floor.image} alt={floor.name} className="w-full block select-none" draggable={false} />
                                {pinsOnFloor.map(s => (
                                    <div
                                        key={s.id}
                                        style={{ left: `${s.pin.x}%`, top: `${s.pin.y}%` }}
                                        className="absolute -translate-x-1/2 -translate-y-full"
                                        title={s.title}
                                    >
                                        <PinMarker number={s.pinNumber} status={s.status} builderResponse={s.builder_response} severity={s.severity} testId={`report-pin-${s.id}`} />
                                    </div>
                                ))}
                                {pinsOnFloor.length > 0 && <PinLegend position="bottom-right" testId={`report-pin-legend-${floor.id}`} />}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


/** Fetch any image url (same-origin /api/files/... or data:) and return a
 *  base64 data URI suitable for jsPDF's addImage. Cookies included so the
 *  authenticated object-storage proxy works. */
async function fetchAsDataUrl(src) {
    if (!src) return null;
    if (src.startsWith('data:')) return src;
    const attempt = async (withCreds) => {
        const res = await fetch(src, withCreds ? { credentials: 'include' } : {});
        if (!res.ok) throw new Error(`image fetch ${res.status}`);
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        // SVGs without explicit width/height rasterize at the browser default
        // viewport (150×119). Read the text, inject width+height attrs sized
        // to our PDF target, and hand back a normalised data URL so <img>
        // rasterizes the vector crisply at that target.
        if (ct.includes('image/svg') || ct.includes('svg+xml')) {
            const text = await res.text();
            const forced = forceSvgDimensionsForPdf(text, 2600);
            return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(forced)));
        }
        const blob = await res.blob();
        return await new Promise((ok, fail) => {
            const fr = new FileReader();
            fr.onload = () => ok(fr.result);
            fr.onerror = fail;
            fr.readAsDataURL(blob);
        });
    };
    try {
        return await attempt(true);
    } catch (err1) {
        // Fallback: cross-origin hosts that don't allow credentialed CORS
        // will fail the first request. Retry without credentials so the
        // floorplan image still lands in the PDF.
        try { return await attempt(false); }
        catch (err2) {
            console.warn('[pdf] fetch failed for', src, err1, err2);
            return null;
        }
    }
}

function forceSvgDimensionsForPdf(svgText, targetDim) {
    try {
        const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
        const svg = doc.documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== 'svg') return svgText;
        const vb = svg.getAttribute('viewBox');
        let vbW = 0, vbH = 0;
        if (vb) {
            const p = vb.split(/[\s,]+/).map(Number);
            if (p.length === 4 && p[2] > 0 && p[3] > 0) { vbW = p[2]; vbH = p[3]; }
        }
        if (!vbW || !vbH) {
            vbW = parseFloat(svg.getAttribute('width')) || targetDim;
            vbH = parseFloat(svg.getAttribute('height')) || targetDim;
        }
        const aspect = vbW / vbH;
        const w = aspect >= 1 ? targetDim : Math.round(targetDim * aspect);
        const h = aspect >= 1 ? Math.round(targetDim / aspect) : targetDim;
        svg.setAttribute('width', String(w));
        svg.setAttribute('height', String(h));
        return new XMLSerializer().serializeToString(svg);
    } catch {
        return svgText;
    }
}

function loadImageElement(dataUrl) {
    return new Promise((ok, fail) => {
        const img = new Image();
        img.onload = () => ok(img);
        img.onerror = fail;
        img.src = dataUrl;
    });
}

/** Fetch → decode → redraw onto an offscreen canvas → re-export as a
 *  guaranteed-valid image data URL. This bypasses jsPDF's flaky handling
 *  of large / unusual image blobs (HEIC, AVIF, progressive JPEGs, etc.).
 *
 *  Output defaults to PNG (lossless) because floorplans are typically
 *  line drawings where JPEG compression introduces visible ringing and
 *  muddies thin wall lines / room labels. maxDim=2600 gives ≈300 DPI
 *  across a 190mm A4 content width so lines stay sharp when printed.
 *  Returns { dataUrl, width, height, format } or null on failure. */
async function loadAndNormalizeImage(src, { maxDim = 2600, format = 'PNG' } = {}) {
    const raw = await fetchAsDataUrl(src);
    if (!raw) return null;
    let img;
    try { img = await loadImageElement(raw); }
    catch (err) { console.warn('[pdf] decode failed for', src, err); return null; }
    if (!img.naturalWidth || !img.naturalHeight) return null;

    // SVG sources are vector and can be rasterized at any resolution
    // without quality loss — so we should upscale them to hit our target
    // DPI. For raster sources (PNG/JPEG/WEBP) we never upscale, since that
    // only invents blur from low-res originals.
    const isSvg = raw.startsWith('data:image/svg');
    const longestEdge = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = isSvg
        ? (maxDim / longestEdge)                       // vector → rasterize at maxDim
        : Math.min(1, maxDim / longestEdge);           // raster → downscale only
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        // High-quality downsampling — default is 'low' (bilinear) which
        // visibly blurs fine lines. 'high' turns on bicubic/Lanczos in
        // most browsers.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        // White background so PNGs with transparent backgrounds don't
        // ink through, and so JPEGs don't turn transparent pixels black.
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const mime = format === 'JPEG' ? 'image/jpeg' : 'image/png';
        const quality = format === 'JPEG' ? 0.95 : undefined;
        const dataUrl = canvas.toDataURL(mime, quality);
        return { dataUrl, width: w, height: h, format };
    } catch (err) {
        console.warn('[pdf] canvas normalize failed for', src, err);
        return null;
    }
}

function guessImageFormat(dataUrl) {
    if (!dataUrl) return 'JPEG';
    if (dataUrl.startsWith('data:image/png')) return 'PNG';
    if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
    return 'JPEG';
}

/** Build the actual PDF document. Returns a Blob. */
async function buildPdfDocument({ project, user, out, done, shareLink }) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentW = pageW - 2 * margin;
    let y = margin;

    // Generate the QR data URL up-front. Renders at a high margin so the
    // shrunk 22mm square in the header stays comfortably scannable.
    let qrDataUrl = null;
    if (shareLink) {
        try {
            qrDataUrl = await QRCode.toDataURL(shareLink, {
                errorCorrectionLevel: 'M',
                margin: 1,
                width: 512,
                color: { dark: '#09090B', light: '#FFFFFF' },
            });
        } catch (err) {
            console.warn('[pdf] QR generation failed', err);
        }
    }
    // Reserve room on the right for the QR + its caption so the property
    // text block doesn't overlap it.
    const qrSize = 22;
    const qrRightPad = qrDataUrl ? qrSize + 4 : 0;

    // --- Header: logo + title block ---
    // The PNG is 962×358 (~2.69:1) — load it as an Image so we can fit it
    // to a fixed width while preserving its real aspect ratio. Hardcoding
    // a 30×20mm box was squashing it vertically on the report.
    const logoData = await fetchAsDataUrl('/defectr-logo.png');
    let logoBlockH = 14;  // fallback height if the load fails
    if (logoData) {
        try {
            const logoImg = await loadImageElement(logoData);
            const logoTargetW = 38;  // mm — slightly larger now that height auto-scales
            const aspect = logoImg.naturalWidth / logoImg.naturalHeight || 2.7;
            logoBlockH = logoTargetW / aspect;
            doc.addImage(logoData, guessImageFormat(logoData), margin, y, logoTargetW, logoBlockH);
        } catch { /* noop — title block below still renders */ }
    }
    // Title sits below the logo with a small gap regardless of how tall
    // the logo ended up rendering.
    const titleY = y + logoBlockH + 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(24, 24, 27);
    doc.text('Snag Report', margin, titleY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(82, 82, 91);
    doc.text(`DEFECTS RECTIFIED  ·  ${new Date().toLocaleDateString()}`, margin, titleY + 5);

    // Right-aligned property info (shifted left by QR width when present)
    const rightX = pageW - margin - qrRightPad;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(24, 24, 27);
    doc.text(project.name || 'Property', rightX, y + 6, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(82, 82, 91);
    let ry = y + 11;
    if (project.address) {
        const addrLines = doc.splitTextToSize(project.address, 60);
        doc.text(addrLines.slice(0, 2), rightX, ry, { align: 'right' });
        ry += 4 * Math.min(2, addrLines.length);
    }
    if (user?.name) doc.text(`By ${user.name}`, rightX, ry, { align: 'right' });

    // --- QR code in header (tap-to-reply portal) ---
    if (qrDataUrl) {
        const qrX = pageW - margin - qrSize;
        const qrY = y;
        try {
            doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize, undefined, 'FAST');
            // Caption underneath
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6);
            doc.setTextColor(31, 165, 207);
            doc.text('SCAN TO REPLY', qrX + qrSize / 2, qrY + qrSize + 2.5, { align: 'center' });
        } catch (err) {
            console.warn('[pdf] QR addImage failed', err);
        }
    }

    y += 40;

    // --- Stats box ---
    const statsH = 18;
    doc.setDrawColor(24, 24, 27);
    doc.setLineWidth(0.4);
    doc.rect(margin, y, contentW, statsH);
    doc.line(margin + contentW / 2, y, margin + contentW / 2, y + statsH);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(82, 82, 91);
    doc.text('OUTSTANDING', margin + 3, y + 5);
    doc.text('COMPLETED', margin + contentW / 2 + 3, y + 5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(229, 57, 53);
    doc.text(String(out.length), margin + 3, y + 14);
    doc.setTextColor(0, 135, 90);
    doc.text(String(done.length), margin + contentW / 2 + 3, y + 14);

    y += statsH + 8;

    // --- Pre-fetch all snag photos in parallel + flatten annotations ---
    // If a snag has photo_annotations, we composite them onto the photo
    // here (vector → flat JPEG) so the PDF embed pipeline doesn't need
    // a separate render path.
    const allSnags = [...out, ...done];
    const photoEntries = await Promise.all(
        allSnags.map(async s => {
            if (!s.photo) return [s.id, null];
            const raw = await fetchAsDataUrl(s.photo);
            if (!raw || !s.photo_annotations || s.photo_annotations.length === 0) return [s.id, raw];
            try {
                const flat = await flattenAnnotatedPhoto(raw, s.photo_annotations);
                return [s.id, flat];
            } catch (err) {
                console.warn('[pdf] annotation flatten failed; falling back to raw photo', s.id, err);
                return [s.id, raw];
            }
        })
    );
    const photoMap = Object.fromEntries(photoEntries);

    // --- Snag group renderer ---
    const drawSectionHeader = (title, count, color) => {
        if (y + 12 > pageH - margin) { doc.addPage(); y = margin; }
        const [r, g, b] = color;
        doc.setFillColor(r, g, b);
        doc.rect(margin, y + 1, 2.5, 6, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(24, 24, 27);
        doc.text(title.toUpperCase(), margin + 6, y + 6);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(82, 82, 91);
        doc.text(`(${count})`, margin + 6 + doc.getTextWidth(title.toUpperCase()) + 3, y + 6);
        y += 10;
    };

    const drawSnagRow = (s, idx, color) => {
        const hasAi = !!s.ai_analysis;
        const hasBuilderRow = !!(s.builder_response || s.disputed);
        // Each strip adds 14mm (single line) — the builder strip can wrap
        // its rejection/dispute reason, so reserve more for that case.
        const builderStripH = hasBuilderRow ? 18 : 0;
        const aiStripH = hasAi ? 16 : 0;
        const rowH = 22 + aiStripH + builderStripH;
        if (y + rowH > pageH - margin) { doc.addPage(); y = margin; }

        // Row background / top border
        doc.setDrawColor(228, 228, 231);
        doc.setLineWidth(0.2);
        doc.line(margin, y, pageW - margin, y);

        // Index
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(82, 82, 91);
        doc.text(String(idx + 1).padStart(2, '0'), margin + 1, y + 8);

        // Photo thumbnail
        const thumbSize = 16;
        const thumbX = margin + 8;
        const thumbY = y + 2.5;
        const photoData = photoMap[s.id];
        if (photoData) {
            try { doc.addImage(photoData, guessImageFormat(photoData), thumbX, thumbY, thumbSize, thumbSize); }
            catch { /* ignore — some formats may fail */ }
            doc.setDrawColor(228, 228, 231);
            doc.setLineWidth(0.2);
            doc.rect(thumbX, thumbY, thumbSize, thumbSize);
        }

        const textX = thumbX + thumbSize + 4;
        const textW = pageW - margin - textX;

        // Pin badge
        let titleOffset = 0;
        if (s.pinNumber) {
            const [r, g, b] = color;
            doc.setFillColor(r, g, b);
            doc.rect(textX, y + 2.5, 6, 5, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(255, 255, 255);
            doc.text(String(s.pinNumber), textX + 3, y + 6, { align: 'center' });
            titleOffset = 8;
        }

        // Title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(24, 24, 27);
        const titleLines = doc.splitTextToSize(s.title || 'Untitled', textW - titleOffset);
        doc.text(titleLines.slice(0, 1), textX + titleOffset, y + 6.5);

        // Meta line: room · pinned on floor
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(82, 82, 91);
        let meta = s.room || '—';
        if (s.floorName) meta += ` · pinned on ${s.floorName}`;
        else if (s.pin) meta += ' · pinned on plan';
        const metaLines = doc.splitTextToSize(meta, textW);
        doc.text(metaLines.slice(0, 1), textX, y + 12);

        // Notes
        if (s.notes) {
            const noteLines = doc.splitTextToSize(s.notes, textW);
            doc.text(noteLines.slice(0, 2), textX, y + 17);
        }

        // AI verdict strip — subtle cyan-tinted band under the snag row.
        // Wrapped in try/catch so a malformed/partial verdict can never
        // crash the whole PDF — we'd rather skip the strip than fail the
        // entire download.
        if (hasAi) {
            try {
                const ai = s.ai_analysis || {};
                const stripY = y + 22;
                doc.setFillColor(232, 245, 251);  // #E8F5FB
                doc.rect(textX, stripY, textW, 14, 'F');
                doc.setDrawColor(31, 165, 207);
                doc.setLineWidth(0.3);
                doc.rect(textX, stripY, textW, 14, 'S');

                const SEV = {
                    urgent:   [229, 57, 53],
                    major:    [245, 158, 11],
                    minor:    [31, 165, 207],
                    cosmetic: [113, 113, 122],
                }[ai.severity] || [31, 165, 207];
                doc.setFillColor(SEV[0], SEV[1], SEV[2]);
                doc.rect(textX + 1.5, stripY + 1.5, 11, 3.5, 'F');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(6);
                doc.setTextColor(255, 255, 255);
                const sevLabel = String(ai.severity || 'minor').toUpperCase();
                doc.text(sevLabel, textX + 7, stripY + 4, { align: 'center' });

                // Cost / responsibility pill
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(7);
                doc.setTextColor(23, 135, 171);
                const metaAi = `${ai.cost_band || '??'}  ·  ${ai.responsibility || '—'}`;
                doc.text(metaAi, textX + 14, stripY + 4);

                // AI badge
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(6);
                doc.setTextColor(23, 135, 171);
                doc.text('AI VERDICT', pageW - margin - 1.5, stripY + 4, { align: 'right' });

                // Cause + fix on one joined line each (truncated)
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                doc.setTextColor(24, 24, 27);
                const causeText = `Cause: ${ai.cause || '—'}`;
                const causeLines = doc.splitTextToSize(causeText, textW - 3);
                if (causeLines.length) doc.text(causeLines.slice(0, 1), textX + 1.5, stripY + 8);
                const fixText = `Fix: ${ai.suggested_fix || '—'}`;
                const fixLines = doc.splitTextToSize(fixText, textW - 3);
                if (fixLines.length) doc.text(fixLines.slice(0, 1), textX + 1.5, stripY + 12);
            } catch (err) {
                console.warn('[pdf] AI verdict strip failed for snag', s?.id, err);
            }
        }

        // Builder response strip — sits beneath the AI verdict strip.
        // Cyan for approved, amber for rejected, blue for disputed.
        // Wrapped in try/catch for the same reason as the AI strip:
        // a malformed response should never break the whole PDF.
        if (hasBuilderRow) {
            try {
                const bResp = (s.builder_response || '').toLowerCase();
                const disputed = !!s.disputed;
                const stripY = y + 22 + aiStripH;
                const stripH = 16;

                // Theme: prefer the "louder" status. Disputed wins
                // visually because that's the thing a third party reads
                // most carefully (rejection that the homeowner is
                // formally challenging).
                let fill, edge, label;
                if (disputed && bResp !== 'approved') {
                    fill = [219, 234, 254]; edge = [37, 99, 235]; label = 'HOMEOWNER DISPUTED';
                } else if (bResp === 'approved') {
                    fill = [232, 245, 251]; edge = [31, 165, 207]; label = 'BUILDER APPROVED';
                } else if (bResp === 'rejected') {
                    fill = [254, 243, 199]; edge = [245, 158, 11]; label = 'BUILDER REJECTED';
                } else {
                    fill = [232, 245, 251]; edge = [31, 165, 207]; label = 'BUILDER RESPONSE';
                }

                doc.setFillColor(fill[0], fill[1], fill[2]);
                doc.rect(textX, stripY, textW, stripH, 'F');
                doc.setDrawColor(edge[0], edge[1], edge[2]);
                doc.setLineWidth(0.3);
                doc.rect(textX, stripY, textW, stripH, 'S');

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(7);
                doc.setTextColor(edge[0], edge[1], edge[2]);
                doc.text(label, textX + 1.5, stripY + 4);

                if (s.builder_name) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(7);
                    doc.setTextColor(82, 82, 91);
                    doc.text(`— ${s.builder_name}`, pageW - margin - 1.5, stripY + 4, { align: 'right' });
                }

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                doc.setTextColor(24, 24, 27);
                let bodyText = '';
                if (bResp === 'rejected') {
                    const reasons = {
                        settlement: 'Normal settlement',
                        owner_damage: 'Owner-induced damage',
                        within_tolerance: 'Within tolerance per NHBC',
                        wear_and_tear: 'Wear & tear',
                        out_of_warranty: 'Out of warranty period',
                        cosmetic_not_defect: 'Cosmetic — not a defect',
                        scope_excluded: 'Out of contract scope',
                        other: 'Other',
                    };
                    const reasonLabel = reasons[s.rejection_reason_code] || 'Other';
                    bodyText = `${reasonLabel}${s.rejection_reason_detail ? ` — ${s.rejection_reason_detail}` : ''}`;
                } else if (bResp === 'approved') {
                    bodyText = 'Builder agreed to fix.';
                }
                if (disputed && s.dispute_reason) {
                    bodyText = (bodyText ? `${bodyText}  ·  ` : '') + `Homeowner: ${s.dispute_reason}`;
                }
                if (bodyText) {
                    const bodyLines = doc.splitTextToSize(bodyText, textW - 3);
                    doc.text(bodyLines.slice(0, 2), textX + 1.5, stripY + 9);
                }
            } catch (err) {
                console.warn('[pdf] builder strip failed for snag', s?.id, err);
            }
        }

        y += rowH;
    };

    const enrichSnag = (s) => ({
        ...s,
        room: project.rooms.find(r => r.id === s.roomId)?.name || '—',
        floorName: s.pin?.floorId ? (project.floorplans?.find(f => f.id === s.pin.floorId)?.name || null) : null,
    });

    if (out.length) {
        drawSectionHeader('Outstanding defects', out.length, [229, 57, 53]);
        out.forEach((s, i) => drawSnagRow(enrichSnag(s), i, [229, 57, 53]));
        y += 4;
    }
    if (done.length) {
        drawSectionHeader('Completed defects', done.length, [0, 135, 90]);
        done.forEach((s, i) => drawSnagRow(enrichSnag(s), i, [0, 135, 90]));
    }

    // --- Floorplans (one per page, pins overlaid) ---
    const floorplans = (project.floorplans || []).filter(f => f.image);
    const failedFloorplans = [];
    for (const floor of floorplans) {
        doc.addPage();
        let fy = margin;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(24, 24, 27);
        doc.text(floor.name || 'Floor plan', margin, fy + 6);
        const pinsOnFloor = project.snags.filter(s => s.pin && s.pin.floorId === floor.id);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(82, 82, 91);
        doc.text(`${pinsOnFloor.length} pin${pinsOnFloor.length === 1 ? '' : 's'}`, pageW - margin, fy + 6, { align: 'right' });
        fy += 10;

        // Normalize the floorplan through a canvas → guaranteed-valid PNG.
        // PNG (lossless) keeps fine wall lines and room labels crisp;
        // JPEG at any quality introduces visible ringing on line art.
        // If ANY step fails, we surface the failure and skip pin overlays.
        const normalized = await loadAndNormalizeImage(floor.image, { maxDim: 2600, format: 'PNG' });
        if (!normalized) {
            failedFloorplans.push(floor.name || 'floor');
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(229, 57, 53);
            doc.text(
                'Floorplan image could not be embedded. Please re-upload this plan from the app and regenerate the PDF.',
                margin, fy + 10, { maxWidth: contentW },
            );
            continue;
        }

        const aspect = normalized.width / Math.max(1, normalized.height);
        const maxW = contentW;
        // Reserve space at the bottom of the page for the pin-index legend
        // so the builder can cross-reference each pin without flipping back
        // to the snag list on page 1. 2-column layout, max 12 rows per col.
        const legendRowH = 5;
        const legendHeaderH = 7;
        const legendGap = 4;
        const legendRows = Math.min(12, Math.ceil(pinsOnFloor.length / 2));
        const legendH = pinsOnFloor.length > 0
            ? legendHeaderH + legendRows * legendRowH + 2
            : 0;
        const maxH = pageH - fy - margin - (legendH ? legendH + legendGap : 0);
        let drawW, drawH;
        if (maxW / aspect <= maxH) { drawW = maxW; drawH = maxW / aspect; }
        else { drawH = maxH; drawW = maxH * aspect; }
        const drawX = margin + (contentW - drawW) / 2;
        const drawY = fy;

        let imageRendered = false;
        try {
            doc.addImage(normalized.dataUrl, normalized.format, drawX, drawY, drawW, drawH, undefined, 'FAST');
            imageRendered = true;
        } catch (err) {
            console.warn('[pdf] addImage failed for floorplan', floor.name, err);
            failedFloorplans.push(floor.name || 'floor');
        }

        // Pin overlays — only when the background actually landed on the
        // page, so we never produce a page of floating pins on white space.
        if (!imageRendered) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(229, 57, 53);
            doc.text(
                'Floorplan image could not be embedded. Please re-upload this plan from the app and regenerate the PDF.',
                margin, fy + 10, { maxWidth: contentW },
            );
            continue;
        }

        for (const s of pinsOnFloor) {
            const px = drawX + (s.pin.x / 100) * drawW;
            const py = drawY + (s.pin.y / 100) * drawH;
            const [r, g, b] = s.status === 'completed' ? [0, 135, 90] : [229, 57, 53];
            doc.setFillColor(r, g, b);
            doc.circle(px, py, 3.4, 'F');
            doc.setDrawColor(255, 255, 255);
            doc.setLineWidth(0.4);
            doc.circle(px, py, 3.4, 'S');
            if (s.pinNumber != null) {
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(7);
                doc.text(String(s.pinNumber), px, py + 1, { align: 'center' });
            }
        }

        // --- Per-pin legend table (2-col) beneath the floorplan image ---
        if (pinsOnFloor.length > 0) {
            const legendY = drawY + drawH + legendGap;
            doc.setDrawColor(24, 24, 27);
            doc.setLineWidth(0.3);
            doc.line(margin, legendY, pageW - margin, legendY);

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(82, 82, 91);
            doc.text('PIN INDEX', margin, legendY + 5);

            const colW = contentW / 2;
            const sorted = [...pinsOnFloor].sort(
                (a, b) => (a.pinNumber ?? 9999) - (b.pinNumber ?? 9999),
            );
            const maxRowsPerCol = legendRows;
            sorted.forEach((s, i) => {
                const col = Math.floor(i / maxRowsPerCol);
                const rowInCol = i % maxRowsPerCol;
                if (col >= 2) return;  // beyond 24 pins — truncate
                const lx = margin + col * colW;
                const ly = legendY + legendHeaderH + rowInCol * legendRowH;

                // Pin badge (matches the circle on the floorplan)
                const [r, g, b] = s.status === 'completed' ? [0, 135, 90] : [229, 57, 53];
                doc.setFillColor(r, g, b);
                doc.circle(lx + 2.2, ly + 1.6, 1.9, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(6);
                doc.text(String(s.pinNumber ?? '?'), lx + 2.2, ly + 2.3, { align: 'center' });

                // Title — truncated to fit the column
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(24, 24, 27);
                const title = s.title || '—';
                const titleLines = doc.splitTextToSize(title, colW - 9);
                doc.text(titleLines[0], lx + 6, ly + 2.3);

                // Strikethrough on completed titles so the status reads at a glance
                if (s.status === 'completed') {
                    const tw = doc.getTextWidth(titleLines[0]);
                    doc.setDrawColor(0, 135, 90);
                    doc.setLineWidth(0.2);
                    doc.line(lx + 6, ly + 1.9, lx + 6 + Math.min(tw, colW - 9), ly + 1.9);
                }
            });

            // Truncation hint when more than 24 pins on one floor
            if (sorted.length > maxRowsPerCol * 2) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(7);
                doc.setTextColor(82, 82, 91);
                doc.text(
                    `+${sorted.length - maxRowsPerCol * 2} more — see full list on page 1.`,
                    pageW - margin, legendY + legendHeaderH + maxRowsPerCol * legendRowH + 1,
                    { align: 'right' },
                );
            }
        }
    }

    // --- Phase 1.5 — Disputes & Rejections appendix ---
    // A dedicated page that surfaces ONLY the rejected-or-disputed
    // defects, sorted by severity, with full reason text. Designed so
    // an NHBC adjudicator, surveyor or solicitor can read it as a
    // standalone artefact.
    const disputed = project.snags.filter(
        s => (s.builder_response === 'rejected') || s.disputed,
    );
    if (disputed.length > 0) {
        doc.addPage();
        let dy = margin;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(24, 24, 27);
        doc.text('Disputes & Rejections', margin, dy + 6);
        dy += 14;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(82, 82, 91);
        const intro =
            `${disputed.length} defect${disputed.length === 1 ? '' : 's'} on this report ` +
            'either had a builder response of "rejected" or were disputed by the homeowner. ' +
            'This page captures the full reason text for an independent third party (NHBC, surveyor, solicitor) to review.';
        const introLines = doc.splitTextToSize(intro, contentW);
        doc.text(introLines, margin, dy);
        dy += introLines.length * 4 + 4;

        const reasons = {
            settlement: 'Normal settlement',
            owner_damage: 'Owner-induced damage',
            within_tolerance: 'Within tolerance per NHBC',
            wear_and_tear: 'Wear & tear',
            out_of_warranty: 'Out of warranty period',
            cosmetic_not_defect: 'Cosmetic — not a defect',
            scope_excluded: 'Out of contract scope',
            other: 'Other',
        };

        disputed.forEach((s, i) => {
            const enriched = enrichSnag(s);
            // 36mm card per defect — paginate when we run out of room.
            if (dy + 38 > pageH - margin) { doc.addPage(); dy = margin; }

            doc.setDrawColor(228, 228, 231);
            doc.setLineWidth(0.3);
            doc.rect(margin, dy, contentW, 34, 'S');

            // Number
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(82, 82, 91);
            doc.text(`#${i + 1}`, margin + 2, dy + 5);

            // Title
            doc.setFontSize(11);
            doc.setTextColor(24, 24, 27);
            const title = (s.title || 'Untitled').slice(0, 100);
            doc.text(title, margin + 12, dy + 5);

            // Status pill (top-right)
            const isDisputed = !!s.disputed && s.builder_response !== 'approved';
            const pillColor = isDisputed ? [37, 99, 235] : [245, 158, 11];
            const pillLabel = isDisputed ? 'DISPUTED' : 'REJECTED';
            doc.setFillColor(pillColor[0], pillColor[1], pillColor[2]);
            const pillW = 22;
            doc.rect(pageW - margin - pillW - 1, dy + 1.5, pillW, 5, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(255, 255, 255);
            doc.text(pillLabel, pageW - margin - pillW / 2 - 1, dy + 5, { align: 'center' });

            // Meta
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(82, 82, 91);
            const metaParts = [enriched.room || '—'];
            if (enriched.floorName) metaParts.push(`Pin ${s.pinNumber || ''} on ${enriched.floorName}`);
            doc.text(metaParts.join('  ·  '), margin + 2, dy + 11);

            // Builder rejection block
            if (s.builder_response === 'rejected') {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(180, 83, 9);
                doc.text(`Builder rejection: ${reasons[s.rejection_reason_code] || 'Other'}`, margin + 2, dy + 17);
                if (s.rejection_reason_detail) {
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(24, 24, 27);
                    const lines = doc.splitTextToSize(s.rejection_reason_detail, contentW - 4);
                    doc.text(lines.slice(0, 2), margin + 2, dy + 21);
                }
                if (s.builder_name) {
                    doc.setFont('helvetica', 'italic');
                    doc.setFontSize(7);
                    doc.setTextColor(82, 82, 91);
                    doc.text(`— ${s.builder_name}`, pageW - margin - 2, dy + 17, { align: 'right' });
                }
            }

            // Homeowner dispute block
            if (s.disputed) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(37, 99, 235);
                const yPos = s.builder_response === 'rejected' ? dy + 28 : dy + 17;
                doc.text('Homeowner dispute:', margin + 2, yPos);
                if (s.dispute_reason) {
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(24, 24, 27);
                    const lines = doc.splitTextToSize(s.dispute_reason, contentW - 4);
                    const yBody = s.builder_response === 'rejected' ? dy + 32 : dy + 21;
                    doc.text(lines.slice(0, 2), margin + 2, yBody);
                }
            }

            dy += 38;
        });
    }

    return { doc, blob: doc.output('blob'), failedFloorplans };
}
